import { BeneficiaryType, Currency } from '@seapoint/server/enums';
import { TransactionView } from '../../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../../utils/exhaustive-check';
import { getAccountDetails } from '../../../../payments/utils/get-account-details';

type AccountDetailsConfig =
  | {
      beneficiaryType: BeneficiaryType.IBAN;
      iban: string;
    }
  | {
      beneficiaryType: BeneficiaryType.SCAN;
      accountNumber: string;
      sortCode: string;
    };

export type BeneficiaryDetails = {
  id: string;
  name: string;
  currency: Currency;
  accountDetails: AccountDetailsConfig;
};

export const createBeneficiaryDetails = (transaction: TransactionView): BeneficiaryDetails | null => {
  const beneficiaryData = transaction?.beneficiary;

  if (!beneficiaryData?.destination?.type || !beneficiaryData.name || !beneficiaryData.destination.currency) {
    return null;
  }

  const { destination } = beneficiaryData;

  return {
    id: beneficiaryData.id ?? '',
    name: beneficiaryData.name,
    currency: destination.currency,
    accountDetails:
      destination.type === BeneficiaryType.IBAN
        ? {
            beneficiaryType: BeneficiaryType.IBAN,
            iban: destination.iban ?? '',
          }
        : {
            beneficiaryType: BeneficiaryType.SCAN,
            accountNumber: destination.accountNumber ?? '',
            sortCode: destination.sortCode ?? '',
          },
  };
};

export const getBeneficiaryAccountDetails = (beneficiary: BeneficiaryDetails) => {
  const { accountDetails } = beneficiary;

  if (accountDetails.beneficiaryType === BeneficiaryType.IBAN) {
    return getAccountDetails({
      type: accountDetails.beneficiaryType,
      iban: accountDetails.iban,
    });
  }

  return getAccountDetails({
    type: accountDetails.beneficiaryType,
    accountNumber: accountDetails.accountNumber,
    sortCode: accountDetails.sortCode,
  });
};

type FormatBeneficiaryDescriptionArgs = {
  beneficiary: BeneficiaryDetails;
  accountDetailsConfig: AccountDetailsConfig;
};

export const formatBeneficiaryDescription = ({
  beneficiary,
  accountDetailsConfig,
}: FormatBeneficiaryDescriptionArgs) => {
  const { beneficiaryType } = accountDetailsConfig;

  switch (beneficiaryType) {
    case BeneficiaryType.IBAN: {
      const { iban } = accountDetailsConfig;

      if (!iban || iban.trim() === '') {
        return '';
      }
      return `${beneficiary.currency} • ${iban}`;
    }
    case BeneficiaryType.SCAN: {
      const { accountNumber, sortCode } = accountDetailsConfig;

      if (!accountNumber || !sortCode || accountNumber.trim() === '' || sortCode.trim() === '') {
        return '';
      }

      return `${beneficiary.currency} • ${accountNumber} • ${sortCode}`;
    }
    default:
      return exhaustiveCheck(beneficiaryType);
  }
};
